import {
  BonusContext,
  BonusScreenMessage,
  ChangeRoundMessage,
  ChangeRoundType,
  IConfiguration,
  INumberFormatter,
  MessagingConstants,
  RoundMessage,
  RoundMessageType,
} from '@cgs/common';
import { BonusResponse } from '@cgs/data-access';
import { Func3 } from '@cgs/shared';
import { BonusResponseWithRules } from '../bonus_game_with_rules';
import { BonusMessageResolver } from './bonus_message_resolver';
import { GenericRoundResolver } from './round/generic_round_resolver';

export class RulesBasedBonusMessageResolver extends BonusMessageResolver {
  protected _configuration: IConfiguration;
  private _genericResolver: GenericRoundResolver;

  constructor(
    bonusContext: BonusContext,
    updateBonusFinishPropertiesWithCurrent: Func3<
      BonusResponse,
      Map<string, any>,
      Map<string, any>,
      void
    > | null,
    configuration: IConfiguration,
    numberFormatter: INumberFormatter
  ) {
    super(bonusContext, updateBonusFinishPropertiesWithCurrent, numberFormatter);
    this._configuration = configuration;
    this._genericResolver = new GenericRoundResolver(numberFormatter);
  }

  updateBonus(bonusResponse: BonusResponseWithRules): BonusScreenMessage[] {
    const messages: BonusScreenMessage[] = [];

    // Use GenericRoundResolver for all rounds
    const roundMessage = this._genericResolver.message(
      this.bonusContext,
      bonusResponse.currentRound!,
      bonusResponse.result
    );
    const roundProperties = roundMessage.properties;
    this.baseProperties(roundProperties, bonusResponse);
    this.bonusContext.add(bonusResponse.currentRound!.roundNumber, roundProperties);
    messages.push(roundMessage);

    // Generate AfterWin message if the round was a win
    if (roundMessage.type === RoundMessageType.Win) {
      const afterWinMessage = RoundMessage.fromProperties(
        RoundMessageType.AfterWin,
        this._genericResolver.properties(
          this.bonusContext,
          bonusResponse.currentRound!,
          bonusResponse.result
        )
      );
      const afterWinProperties = afterWinMessage.properties;
      this.baseProperties(afterWinProperties, bonusResponse);
      messages.push(afterWinMessage);
    }

    // Handle round transitions
    if (
      bonusResponse.nextRound &&
      bonusResponse.nextRound.roundNumber > bonusResponse.currentRound!.roundNumber
    ) {
      // Go to the next round
      const nextRoundProperties = this._genericResolver.properties(
        this.bonusContext,
        bonusResponse.nextRound,
        bonusResponse.result
      );
      this.baseProperties(nextRoundProperties, bonusResponse);
      this.bonusContext.add(bonusResponse.nextRound.roundNumber, nextRoundProperties);

      const round = this._configuration.rounds.find((round) =>
        round.serverIndexes.includes(bonusResponse.nextRound!.roundNumber)
      );

      if (round) {
        const changeRoundMessage = new ChangeRoundMessage(
          ChangeRoundType.Specific,
          this._configuration.rounds.indexOf(round)
        );
        changeRoundMessage.currentRoundProperties = roundProperties;
        changeRoundMessage.nextRoundProperties = nextRoundProperties;
        messages.push(changeRoundMessage);
      } else {
        const changeRoundMessage1 = new ChangeRoundMessage(
          ChangeRoundType.Next,
          bonusResponse.nextRound.roundNumber
        );
        changeRoundMessage1.currentRoundProperties = roundProperties;
        changeRoundMessage1.nextRoundProperties = nextRoundProperties;
        messages.push(changeRoundMessage1);
      }
    }

    // Handle bonus finish
    if (bonusResponse.bonusFinished) {
      const curProperties = this._genericResolver.properties(
        this.bonusContext,
        bonusResponse.currentRound!,
        bonusResponse.result
      );
      curProperties.set(
        MessagingConstants.previousRoundNumber,
        bonusResponse.currentRound!.roundNumber
      );
      this.baseProperties(curProperties, bonusResponse);

      const finishProperties = this._genericResolver.resultProperties(
        this.bonusContext,
        bonusResponse.result || {
          bonusWin: 0,
          totalWin: 0,
          multiplier: 1,
          bet: 0,
          lines: 0,
          winName: '',
          betCalculation: '',
          additionWin: 0,
          specGroups: null,
          winLines: null,
        }
      );
      finishProperties.set(
        MessagingConstants.previousRoundNumber,
        bonusResponse.currentRound!.roundNumber
      );
      this.baseProperties(finishProperties, bonusResponse);

      if (this.updateBonusFinishPropertiesWithCurrent) {
        this.updateBonusFinishPropertiesWithCurrent(bonusResponse, finishProperties, curProperties);
      }

      // Navigate to win/lose scene based on round result
      const finalSceneIndex = this.determineFinalScene(bonusResponse, roundMessage);

      if (finalSceneIndex !== -1 && bonusResponse.currentRound!.roundNumber !== finalSceneIndex) {
        const changeRoundMessage2 = new ChangeRoundMessage(
          ChangeRoundType.Specific,
          finalSceneIndex
        );
        changeRoundMessage2.currentRoundProperties = curProperties;
        changeRoundMessage2.nextRoundProperties = finishProperties;
        messages.push(changeRoundMessage2);
      } else {
        const changeRoundMessage3 = new ChangeRoundMessage(ChangeRoundType.Specific);
        changeRoundMessage3.currentRoundProperties = curProperties;

        finishProperties.set(MessagingConstants.previousRoundNumber, 10);
        changeRoundMessage3.nextRoundProperties = finishProperties;
        messages.push(changeRoundMessage3);
      }
    }

    return messages;
  }

  private determineFinalScene(_bonusResponse: BonusResponse, _roundMessage: RoundMessage): number {
    return -1;
  }
}
